import * as React from "react"
import { useEffect, useState } from "react"
import {
	Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
	DialogContent, DialogTitle, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material"
import MergeIcon from '@mui/icons-material/CallMerge'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import RefreshIcon from '@mui/icons-material/Refresh'

import { BACKEND_APIS } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"
import { vtApi as api } from "../api"


// Local YYYY-MM-DD helpers — no timezone drift (avoid toISOString which
// shifts across midnight in non-UTC locales).
function ymd(d) {
	const y = d.getFullYear()
	const m = String(d.getMonth() + 1).padStart(2, '0')
	const day = String(d.getDate()).padStart(2, '0')
	return `${y}-${m}-${day}`
}
function prevDay(dateStr) {
	if (!dateStr) return ''
	const d = new Date(dateStr + 'T00:00:00')
	d.setDate(d.getDate() - 1)
	return ymd(d)
}

// Brand names on a resource (objects or raw strings), for the quick filters.
function brandNames(item) {
	return (item?.tagging_json?.brands || [])
		.map(b => (typeof b === 'object' ? b?.name : b))
		.filter(Boolean)
}

// Hamming distance between two 16-hex-char (64-bit) pHashes. Returns null
// if either is missing/malformed. Mirrors the back's _hamming_distance.
function hammingHex(h1, h2) {
	if (!h1 || !h2) return null
	try {
		let x = BigInt('0x' + h1) ^ BigInt('0x' + h2)
		let c = 0
		while (x > 0n) { c += Number(x & 1n); x >>= 1n }
		return c
	} catch {
		return null
	}
}


export default function ResourceMergePage() {
	const { showToast } = useAppToast()
	const [retailers, setRetailers] = useState([])
	const [idRetailer, setIdRetailer] = useState('')
	const [dateA, setDateA] = useState('')
	const [dateB, setDateB] = useState('')
	const [sideA, setSideA] = useState([])
	const [sideB, setSideB] = useState([])
	const [thresholds, setThresholds] = useState({})
	const [selA, setSelA] = useState(null)   // id_resource selected on side A
	const [selB, setSelB] = useState(null)
	const [loading, setLoading] = useState(false)
	const [merging, setMerging] = useState(false)
	const [loaded, setLoaded] = useState(false)
	// Quick client-side filters over the already-loaded data.
	const [filterKind, setFilterKind] = useState('')
	const [filterBrand, setFilterBrand] = useState('')
	// Lightbox: null, {mode:'single', src}, or {mode:'pair', a, b, labelA, labelB}.
	const [lightbox, setLightbox] = useState(null)
	// Auto-merge preview: proposed brand-aware groups + per-group include flag.
	const [preview, setPreview] = useState(null)       // {clusters:[...]} or null
	const [previewChecked, setPreviewChecked] = useState({})  // idx -> bool
	const [previewLoading, setPreviewLoading] = useState(false)
	const [applying, setApplying] = useState(false)

	useEffect(() => {
		if (!lightbox) return
		const onKey = (e) => { if (e.key === 'Escape') setLightbox(null) }
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [lightbox])

	// Retailer catalogue for the dropdown.
	useEffect(() => {
		api.get(`${BACKEND_APIS.VT}/admin/retailers`).then(r => {
			if (r.ok) setRetailers(r.data?.items || [])
		})
	}, [])

	// Picking date A auto-fills date B with the previous day (editable).
	const onDateA = (v) => {
		setDateA(v)
		if (v && !dateB) setDateB(prevDay(v))
	}

	const canLoad = idRetailer && dateA && dateB && dateA !== dateB

	const load = async () => {
		if (!canLoad) return
		setLoading(true)
		setSelA(null); setSelB(null)
		setFilterKind(''); setFilterBrand('')
		try {
			const r = await api.get(
				`${BACKEND_APIS.VT}/admin/resources/day-diff`
				+ `?id_retailer=${idRetailer}&date_a=${dateA}&date_b=${dateB}`
			)
			if (!r.ok) { showToast.error('Load failed: ' + r.error); return }
			setSideA(r.data?.side_a || [])
			setSideB(r.data?.side_b || [])
			setThresholds(r.data?.thresholds || {})
			setLoaded(true)
		} finally {
			setLoading(false)
		}
	}

	const doMerge = async () => {
		if (!selA || !selB) return
		const a = sideA.find(x => x.id_resource === selA)
		const b = sideB.find(x => x.id_resource === selB)
		if (!a || !b) return
		// Survivor: the validated one if exactly one is validated; else let
		// the back pick (highest count_seen).
		let target_id
		if (!a.needs_qa && b.needs_qa) target_id = a.id_resource
		else if (a.needs_qa && !b.needs_qa) target_id = b.id_resource
		setMerging(true)
		try {
			const r = await api.post(`${BACKEND_APIS.VT}/admin/resources/merge`, {
				ids: [selA, selB],
				target_id,
			})
			if (!r.ok) { showToast.error('Merge failed: ' + r.error); return }
			showToast.success(`Merged into resource #${r.data?.target_id ?? '?'}.`)
			// Both drop out of the "only-in-one-day" sets after merge, so a
			// reload leaves the panels clean.
			await load()
		} finally {
			setMerging(false)
		}
	}

	// Auto-merge: ask the back for brand-aware merge groups over exactly what's
	// on screen (the type/brand-filtered A+B sets), then preview par-by-par.
	const autoMergePreview = async (ids) => {
		if (!ids.length) { showToast.info('Nothing on screen to auto-merge.'); return }
		setPreviewLoading(true)
		try {
			const r = await api.post(`${BACKEND_APIS.VT}/admin/resources/consolidate-preview`, {
				id_resources: ids,
			})
			if (!r.ok) { showToast.error('Preview failed: ' + r.error); return }
			const clusters = r.data?.clusters || []
			if (clusters.length === 0) { showToast.info('No brand-aware duplicates found on screen.'); return }
			const checked = {}
			clusters.forEach((_, i) => { checked[i] = true })
			setPreview({ clusters })
			setPreviewChecked(checked)
		} finally {
			setPreviewLoading(false)
		}
	}

	const applyPreview = async () => {
		const groups = (preview?.clusters || [])
			.filter((_, i) => previewChecked[i])
			.map(c => c.ids)
		if (!groups.length) { showToast.info('No groups selected.'); return }
		setApplying(true)
		try {
			const r = await api.post(`${BACKEND_APIS.VT}/admin/resources/consolidate-apply`, { groups })
			if (!r.ok) { showToast.error('Auto-merge failed: ' + r.error); return }
			const { merged_groups, resources_removed } = r.data || {}
			showToast.success(`Auto-merged ${merged_groups} group(s), removed ${resources_removed} duplicate(s).`)
			setPreview(null)
			await load()
		} finally {
			setApplying(false)
		}
	}

	// Filter options + filtered views (client-side, over loaded data).
	const kindOptions = Array.from(new Set(
		[...sideA, ...sideB].map(it => it.parent_kind).filter(Boolean)
	)).sort()
	const brandOptions = Array.from(new Set(
		[...sideA, ...sideB].flatMap(brandNames)
	)).sort((a, b) => a.localeCompare(b))
	const applyFilters = (list) => list.filter(it =>
		(!filterKind || it.parent_kind === filterKind)
		&& (!filterBrand || brandNames(it).includes(filterBrand))
	)
	const filteredA = applyFilters(sideA)
	const filteredB = applyFilters(sideB)
	const hasData = sideA.length > 0 || sideB.length > 0
	// Lookup for the auto-merge preview modal (crops/labels by id) + the ids
	// currently on screen (respecting the type/brand filters).
	const byId = {}
	for (const r of [...sideA, ...sideB]) byId[r.id_resource] = r
	const onScreenIds = [...filteredA, ...filteredB].map(x => x.id_resource)

	// Pair diagnostic: when one resource is picked on each side, explain the
	// pHash distance vs the auto-merge gate so the operator understands why
	// the matcher didn't unify them. The threshold applied is the one for the
	// NEWER capture's kind (that's the gate that ran when it appeared).
	const pickedA = selA ? sideA.find(x => x.id_resource === selA) : null
	const pickedB = selB ? sideB.find(x => x.id_resource === selB) : null
	let pairInfo = null
	if (pickedA && pickedB) {
		const dist = hammingHex(pickedA.phash, pickedB.phash)
		const newer = (pickedA.first_seen_at || '') >= (pickedB.first_seen_at || '') ? pickedA : pickedB
		const kind = newer.parent_kind || 'default'
		const thr = thresholds[kind] ?? thresholds.default
		pairInfo = {
			dist, kind, thr,
			sameKind: pickedA.parent_kind === pickedB.parent_kind,
			overThreshold: dist != null && thr != null && dist > thr,
		}
	}

	// Click on a crop → lightbox. When BOTH sides have a pick, show the two
	// selected crops side by side for comparison; otherwise zoom the clicked one.
	const openZoom = (clickedCrop) => {
		if (pickedA?.crop_inlined && pickedB?.crop_inlined) {
			setLightbox({
				mode: 'pair',
				a: pickedA.crop_inlined, b: pickedB.crop_inlined,
				labelA: `${dateA} · #${pickedA.id_resource}`,
				labelB: `${dateB} · #${pickedB.id_resource}`,
			})
		} else if (clickedCrop) {
			setLightbox({ mode: 'single', src: clickedCrop })
		}
	}

	return <PageWrapper {...vtPageProps('Resource Merge', 'Deduplicate creatives that appear on one day but not the other')}>
		<Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
			<Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
				<TextField
					size="small" select label="Retailer"
					value={idRetailer}
					onChange={e => setIdRetailer(e.target.value)}
					sx={{ width: 240 }}
				>
					<MenuItem value="">Select retailer…</MenuItem>
					{retailers.map(r => (
						<MenuItem key={r.id_retailer} value={r.id_retailer}>{r.name}</MenuItem>
					))}
				</TextField>
				<TextField
					size="small" type="date" label="Date A"
					value={dateA}
					onChange={e => onDateA(e.target.value)}
					InputLabelProps={{ shrink: true }}
					sx={{ width: 170 }}
				/>
				<TextField
					size="small" type="date" label="Date B"
					value={dateB}
					onChange={e => setDateB(e.target.value)}
					InputLabelProps={{ shrink: true }}
					helperText="Defaults to the day before A"
					sx={{ width: 170 }}
				/>
				<Button variant="contained" onClick={load} disabled={!canLoad || loading}>
					Load
				</Button>
				<Box sx={{ flex: 1 }} />
				<Button
					variant="outlined" color="secondary" startIcon={<AutoAwesomeIcon />}
					onClick={() => autoMergePreview(onScreenIds)}
					disabled={!hasData || previewLoading}
				>
					{previewLoading ? 'Analyzing…' : 'Auto-merge (on screen)'}
				</Button>
				<Button
					variant="contained" color="success" startIcon={<MergeIcon />}
					onClick={doMerge} disabled={!selA || !selB || merging}
				>
					Merge selected
				</Button>
			</Stack>

			{!loading && hasData && (
				<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
					<Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.06 }}>
						Quick filters
					</Typography>
					<TextField
						size="small" select label="Type"
						value={filterKind}
						onChange={e => setFilterKind(e.target.value)}
						sx={{ width: 180 }}
					>
						<MenuItem value="">All types</MenuItem>
						{kindOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
					</TextField>
					<TextField
						size="small" select label="Brand (included)"
						value={filterBrand}
						onChange={e => setFilterBrand(e.target.value)}
						sx={{ width: 220 }}
					>
						<MenuItem value="">All brands</MenuItem>
						{brandOptions.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
					</TextField>
					{(filterKind || filterBrand) && (
						<Button size="small" onClick={() => { setFilterKind(''); setFilterBrand('') }}>
							Clear filters
						</Button>
					)}
				</Stack>
			)}

			{loading && (
				<Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>
			)}

			{!loading && loaded && sideA.length === 0 && sideB.length === 0 && (
				<Alert severity="success" sx={{ mt: 2 }}>
					No cross-day duplicate candidates — every resource on these two days is either shared or unique.
				</Alert>
			)}

			{pairInfo && (
				<Alert
					severity={pairInfo.dist == null ? 'info' : (pairInfo.overThreshold ? 'info' : 'warning')}
					sx={{ mb: 2 }}
				>
					{pairInfo.dist == null ? (
						<>No se puede calcular la distancia (falta pHash en alguno).</>
					) : (
						<>
							<strong>Distancia pHash: {pairInfo.dist}/64 bits</strong>
							&nbsp;·&nbsp;umbral auto-merge ({pairInfo.kind}): ≤ {pairInfo.thr}
							{!pairInfo.sameKind && ' · (tipos distintos: se usa el umbral del más reciente)'}
							<br />
							{pairInfo.overThreshold
								? `No se unieron automáticamente: la distancia supera el umbral (${pairInfo.dist} > ${pairInfo.thr}) — el matcher las considera creativos distintos. Revísalas y únelas manualmente si son la misma.`
								: `Distancia dentro del umbral (${pairInfo.dist} ≤ ${pairInfo.thr}): deberían haberse unido. Probable causa: aparecieron en días sin solape de captura, o una se creó antes de existir la otra.`}
						</>
					)}
				</Alert>
			)}

			{!loading && hasData && (
				<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
					<DayPanel
						title={`Only on ${dateA}`}
						items={filteredA}
						selected={selA}
						onSelect={setSelA}
						onZoom={openZoom}
					/>
					<DayPanel
						title={`Only on ${dateB}`}
						items={filteredB}
						selected={selB}
						onSelect={setSelB}
						onZoom={openZoom}
					/>
				</Stack>
			)}
		</Box>

		{lightbox && (
			<Box
				onClick={() => setLightbox(null)}
				sx={{
					position: 'fixed', inset: 0, zIndex: 1500,
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					gap: 3, bgcolor: 'rgba(12, 26, 62, 0.88)', cursor: 'zoom-out', p: 4,
				}}
			>
				{lightbox.mode === 'pair' ? (
					<>
						<LightboxPane src={lightbox.a} label={lightbox.labelA} />
						<LightboxPane src={lightbox.b} label={lightbox.labelB} />
					</>
				) : (
					<LightboxPane src={lightbox.src} />
				)}
				<Typography sx={{
					position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
					color: 'rgba(255,255,255,0.75)', fontSize: 11, textTransform: 'uppercase',
					letterSpacing: 0.05, pointerEvents: 'none',
				}}>click or ESC to close</Typography>
			</Box>
		)}

		<Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
			<DialogTitle>
				Auto-merge preview — {(preview?.clusters || []).length} group(s)
				<Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
					Same type + identical brand set + pHash within the relaxed gate. Uncheck any you don't want merged.
				</Typography>
			</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={1.5}>
					{(preview?.clusters || []).map((c, i) => (
						<Paper key={i} variant="outlined" sx={{ p: 1, opacity: previewChecked[i] ? 1 : 0.5 }}>
							<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
								<Checkbox
									size="small" checked={!!previewChecked[i]}
									onChange={e => setPreviewChecked({ ...previewChecked, [i]: e.target.checked })}
								/>
								<Chip size="small" color="primary" variant="outlined" label={c.parent_kind} />
								<Typography variant="body2" sx={{ fontWeight: 600 }}>
									{(c.brands || []).slice(0, 4).join(', ') || '(no brand names)'}
									{c.title ? ` · “${c.title}”` : ''}
								</Typography>
								<Chip size="small" variant="outlined" label={`${c.size} → 1`} />
								{c.by_title && (
									<Chip size="small" color="success" variant="outlined" label="by title" />
								)}
								<Chip
									size="small"
									color={(!c.by_title && c.max_dist >= c.gate) ? 'warning' : 'default'}
									variant="outlined"
									label={`max dist ${c.max_dist}/${c.gate}`}
								/>
							</Stack>
							<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, pl: 5 }}>
								{c.ids.map(id => {
									const it = byId[id]
									return <Box key={id} sx={{ textAlign: 'center' }}>
										<Box sx={{
											width: 130, height: 100, border: '1px solid', borderColor: 'divider',
											borderRadius: 1, overflow: 'hidden', bgcolor: '#f8f9fb',
											display: 'flex', alignItems: 'center', justifyContent: 'center',
											cursor: it?.crop_inlined ? 'zoom-in' : 'default',
										}}
											onClick={() => it?.crop_inlined && setLightbox({ mode: 'single', src: it.crop_inlined })}
										>
											{it?.crop_inlined
												? <img src={it.crop_inlined} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
												: <Typography variant="caption" color="text.disabled">#{id}</Typography>}
										</Box>
										<Typography variant="caption" color="text.secondary">
											#{id}{it && !it.needs_qa ? ' ✓' : ''}
										</Typography>
									</Box>
								})}
							</Stack>
						</Paper>
					))}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setPreview(null)} disabled={applying}>Cancel</Button>
				<Button
					variant="contained" color="success" startIcon={<MergeIcon />}
					onClick={applyPreview}
					disabled={applying || Object.values(previewChecked).every(v => !v)}
				>
					{applying ? 'Merging…' : `Merge ${Object.values(previewChecked).filter(Boolean).length} group(s)`}
				</Button>
			</DialogActions>
		</Dialog>
	</PageWrapper>
}


function LightboxPane({ src, label }) {
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, maxWidth: label ? '48vw' : '95vw' }}>
		{label && (
			<Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 700 }}>{label}</Typography>
		)}
		<Box component="img" src={src} alt=""
			sx={{
				maxWidth: '100%', maxHeight: '88vh', objectFit: 'contain',
				boxShadow: '0 12px 60px rgba(0,0,0,0.5)', borderRadius: 1, background: '#fff',
			}}
		/>
	</Box>
}


function DayPanel({ title, items, selected, onSelect, onZoom }) {
	return <Box sx={{ flex: 1, minWidth: 0 }}>
		<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
			<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
			<Chip size="small" label={`${items.length}`} />
		</Stack>
		<Stack spacing={1}>
			{items.map(it => (
				<ResourceCard
					key={it.id_resource}
					item={it}
					selected={selected === it.id_resource}
					onClick={() => onSelect(selected === it.id_resource ? null : it.id_resource)}
					onZoom={onZoom}
				/>
			))}
		</Stack>
	</Box>
}


function ResourceCard({ item, selected, onClick, onZoom }) {
	const brands = (item.tagging_json?.brands || [])
		.map(b => (typeof b === 'object' ? b?.name : b)).filter(Boolean)
	const cats = item.tagging_json?.categories || []
	return <Paper
		onClick={onClick}
		sx={{
			p: 1, display: 'flex', gap: 1.5, cursor: 'pointer',
			border: '2px solid', borderColor: selected ? 'success.main' : 'divider',
			bgcolor: selected ? '#e8f5e9' : 'background.paper',
			'&:hover': { borderColor: selected ? 'success.main' : 'primary.light' },
		}}
	>
		<Box
			onClick={item.crop_inlined ? (e) => { e.stopPropagation(); onZoom && onZoom(item.crop_inlined) } : undefined}
			title={item.crop_inlined ? 'Click to zoom' : undefined}
			sx={{
				flex: '0 0 120px', height: 90, borderRadius: 1, overflow: 'hidden',
				bgcolor: '#f8f9fb', display: 'flex', alignItems: 'center', justifyContent: 'center',
				cursor: item.crop_inlined ? 'zoom-in' : 'default',
			}}
		>
			{item.crop_inlined
				? <img src={item.crop_inlined} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
				: <Typography variant="caption" color="text.disabled">no crop</Typography>}
		</Box>
		<Box sx={{ minWidth: 0, flex: 1 }}>
			<Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 0.5 }}>
				<Chip size="small" variant="outlined" label={`#${item.id_resource}`} />
				{item.parent_kind && <Chip size="small" color="primary" variant="outlined" label={item.parent_kind} />}
				<Chip
					size="small"
					color={item.needs_qa ? 'warning' : 'success'}
					variant="outlined"
					label={item.needs_qa ? 'needs QA' : 'validated'}
				/>
				{(item.count_seen ?? 0) > 0 && (
					<Chip size="small" variant="outlined" label={`seen ${item.count_seen}×`} />
				)}
			</Stack>
			{brands.length > 0 && (
				<Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
					{brands.join(', ')}
				</Typography>
			)}
			{cats.length > 0 && (
				<Typography variant="caption" color="text.secondary">{cats.join(' · ')}</Typography>
			)}
		</Box>
	</Paper>
}
